<template>
    <BlockUI :blocked="isMoving">
        <template #default>
            <Navigation v-if="mode === 'live' && stek?.identity" />
            <c-stekz-tree
                v-else-if="mode === 'design'"
                ref="stekztree"
                :data-stek-id="stek?.identity"
                :data-selected-domain-id="domain?.identity ?? ''"
                :data-selected-product-id="product?.identity ?? ''"
                :data-selected-process-id="process?.identity ?? ''"
            ></c-stekz-tree>
            <div v-if="isMoving" class="moving-overlay">
                <ProgressSpinner />
            </div>
        </template>
    </BlockUI>
    <div>
        <Menu v-if="subStageMenuItems.length > 0" :model="subStageMenuItems" />
    </div>

    <!-- mode -->
    <div id="mode">
        <!-- change mode button -->
        <Button
            type="button"
            :icon="modeIcon"
            class="p-button-text"
            :label="mode"
            @mouseenter="toggle($event, 'modeMenu')"
            aria-haspopup="true"
            aria-controls="overlay_modemenu"
        />
        <!-- menu items of modes -->
        <Menu
            v-if="stek?.identity"
            ref="modeMenu"
            :model="modeMenuItems"
            :popup="true"
            id="overlay_modemenu"
        >
            <template #item="{ item, props }">
                <a
                    v-ripple
                    class="flex items-center"
                    :class="{
                        'active-mode': item.label.toLowerCase() === mode,
                    }"
                    v-bind="props.action"
                    v-tooltip.right="item.tooltip"
                    :style="
                        item.disabled
                            ? {
                                  'pointer-events': 'auto',
                                  'cursor': 'not-allowed',
                                  'opacity': '0.6',
                              }
                            : {}
                    "
                >
                    <span v-if="item.icon" :class="item.icon" />
                    <span>{{ item.label }}</span>
                </a>
            </template>
        </Menu>
    </div>

    <!-- Delete confirmation -->
    <ConfirmationDialog
        :displayConfirmation="!!entityToDelete"
        :displaySpinner="isLoading"
        :message="`Are you sure you want to delete ${entityToDelete?.entityType} '${entityToDelete?.name}'?`"
        autofocus="close"
        @close-confirmation="entityToDelete = null"
        @success-confirm="deleteEntity"
    />

    <!-- Move confirmation -->
    <ConfirmationDialog
        :displayConfirmation="showMoveConfirmation"
        :displaySpinner="isLoading"
        :title="`Move '${entityMoveInfo?.entity.name}' to '${entityMoveInfo?.target.name}'`"
        :message="`Moving an entity to a different domain will reset it's history. Are you sure you want to proceed?`"
        autofocus="close"
        @close-confirmation="
            () => {
                showMoveConfirmation = false;
                entityMoveInfo = null;
                isMoving = false;
            }
        "
        @success-confirm="moveEntity"
    />
</template>

<script setup lang="ts">
import { whenever } from "@vueuse/core";

import BlockUI from "primevue/blockui";
import type { MenuItem } from "primevue/menuitem";
import ProgressSpinner from "primevue/progressspinner";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { api } from "@/lib/openapi";
import Navigation from "@/live/components/Navigation.vue";
import { baseSuccessToastMessage } from "@/primevue/init";
import ConfirmationDialog from "@/shared/ConfirmationDialog.vue";
import { useDomainsManager, useDomainsStore } from "@/stores/domains-store";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import {
    useProcessesManager,
    useProcessesStore,
} from "@/stores/processes-store";
import { useProductsManager, useProductsStore } from "@/stores/products-store";
import { useReleaseStore } from "@/stores/release-store";
import "@/wc/stekz-tree/index.js";

const route = useRoute();
const { stek } = useStekManager();
const { organization } = useOrganizationManager();
const { domain, product, process } = useCurrentEntityManager();

const { remove: removeDomain } = useDomainsStore();
const { isLoading: isDomainsLoading } = useDomainsManager();
const { remove: removeProduct } = useProductsStore();
const { isLoading: isProductsLoading } = useProductsManager();
const { remove: removeProcess } = useProcessesStore();
const { isLoading: isProcessesLoading } = useProcessesManager();

const isMoving = ref(false);

const mode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode || "design";
});
const modeIcon = computed(() =>
    mode.value === "live" ? "pi pi-play" : "pi pi-eye"
);

// In Live mode, a release must always be selected.
// Watch both mode and releases: handles mode switch, page refresh, and late-loading releases.
const releaseStore = useReleaseStore();

watch(
    [mode, () => releaseStore.releases],
    ([currentMode]) => {
        if (
            currentMode === "live" &&
            !releaseStore.activeRelease &&
            releaseStore.releases.length > 0
        ) {
            releaseStore.setActiveRelease(releaseStore.releases[0].name);
        }
    },
    { immediate: true }
);

const isLoading = computed(() => {
    return (
        isDomainsLoading.value ||
        isProductsLoading.value ||
        isProcessesLoading.value ||
        isMoving.value
    );
});

const modeMenuItems = computed<MenuItem[]>(() => {
    if (!organization.value?.name) {
        return [];
    }

    if (!stek.value?.name) {
        return [];
    }

    return [
        {
            label: "Design",
            icon: "pi pi-eye",
            command: () => {
                router.push({
                    name: "stekPage",
                    params: {
                        orgName: organization.value?.name,
                        stekName: stek.value?.name,
                    },
                });
            },
        },
        {
            label: "Live",
            icon: "pi pi-play",
            disabled: stek.value?.live_mode_enabled === false,
            tooltip:
                stek.value?.live_mode_enabled === false
                    ? "Go to Stek Settings to enable Live mode."
                    : undefined,
            command: () => {
                router.push({
                    name: "livePage",
                    params: {
                        orgName: organization.value?.name,
                        stekName: stek.value?.name,
                    },
                });
            },
        },
    ];
});
const subStageMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    const organizationName = organization.value?.name;
    if (!organizationName) {
        return [];
    }

    // Dashboard button removed - navigation is handled by LiveNavigation tree
    return items;
});

const menu = ref();
const modeMenu = ref();
const router = useRouter();

const entityToDelete = ref();

const showMoveConfirmation = ref(false);
const entityMoveInfo = ref();

const stekztree = ref();

const onEntityMoved = (entityInfo) => {
    entityMoveInfo.value = entityInfo;

    let isCrossDomainMove = false;

    if (entityInfo.entityType === EntityType.PROCESS) {
        isCrossDomainMove =
            entityInfo.entity?.product?.domain?.identity !==
            entityInfo.target?.domain?.identity;
    } else if (entityInfo.entityType === EntityType.PRODUCT) {
        isCrossDomainMove =
            entityInfo.entity?.domain?.identity !== entityInfo.target?.identity;
    } else {
        isCrossDomainMove = false;
    }

    if (isCrossDomainMove) {
        // If the move is cross-domain, we need to show a confirmation dialog.
        showMoveConfirmation.value = true;
    } else {
        // If the move is not cross-domain, we can directly proceed with the move.
        showMoveConfirmation.value = false;
        moveEntity();
    }
};

onMounted(async () => {
    // Until we have integrated inline entity creation in the tree,
    // make use of creation modals.
    whenever(
        stekztree,
        () => {
            stekztree.value.addEventListener("delete", (e: CustomEvent) => {
                entityToDelete.value = e.detail;
            });
            stekztree.value.addEventListener("move", (e: CustomEvent) => {
                onEntityMoved(e.detail);
            });
            stekztree.value.addEventListener(
                "toast-request",
                (e: CustomEvent) => {
                    toast.add({
                        ...baseSuccessToastMessage,
                        severity: e.detail.type,
                        summary: e.detail.message,
                    });
                }
            );
        },
        { immediate: true }
    );
});

const toast = useToast();

const toggle = (event: Event, menuName: string) => {
    const activeEle = document.getElementsByClassName("active");
    if (activeEle.length > 0) {
        const activeEle = document.querySelector(".active");
        activeEle?.classList.remove("active");
    }
    switch (menuName) {
        case "menu":
            menu.value.toggle(event);
            break;
        case "modeMenu":
            modeMenu.value.toggle(event);
            break;
        default:
            // Menu not found;
            break;
    }
};

const moveEntity = async () => {
    try {
        isMoving.value = true;
        const { entity, target } = entityMoveInfo.value;
        if (entityMoveInfo.value.entityType === EntityType.PROCESS) {
            await api.startProcessProcessMgtMoveProcessStartPost({
                body: {
                    process_id: entity.identity,
                    target_product_id: target.identity,
                },
            });
        } else if (entityMoveInfo.value.entityType === EntityType.PRODUCT) {
            await api.startProductProductMgtMoveProductStartPost({
                body: {
                    product_id: entity.identity,
                    target_domain_id: target.identity,
                },
            });
        } else {
            console.error("Unknown entity type for move operation");
        }

        // Refresh the tree after successful move
        await stekztree.value?.refreshTree?.();
    } finally {
        isMoving.value = false;
        showMoveConfirmation.value = false;
        entityMoveInfo.value = null;
    }
};

const deleteEntity = async () => {
    if (!entityToDelete.value) return;
    if (!organization.value) return;

    const baseParams = {
        orgName: organization.value?.name,
        stekName: stek.value?.name,
    };

    if (entityToDelete.value.entityType === "domain") {
        await removeDomain(entityToDelete.value.identity);
        await router.push({
            name: "stekPage",
            params: {
                ...baseParams,
            },
        });
    } else if (entityToDelete.value.entityType === "product") {
        await removeProduct(entityToDelete.value.identity);
        await router.push({
            name: "domainPage",
            params: {
                ...baseParams,
                domainId: domain.value.identity,
            },
        });
    } else if (entityToDelete.value.entityType === "process") {
        await removeProcess(entityToDelete.value.identity);
        await router.push({
            name: "productPage",
            params: {
                ...baseParams,
                productId: product.value.identity,
            },
        });
    } else {
        console.error("Unknown entity type");
    }
    entityToDelete.value = null;
};
</script>

<style scoped lang="scss">
.p-blockui,
c-stekz-tree {
    overflow-y: auto;
    display: block;
    height: 100%;
}

.profile-link {
    color: unset;
    text-decoration: inherit;
}

#mode {
    width: 100%;

    button {
        width: 100%;
        text-transform: capitalize;
    }
}

.active-mode {
    background-color: var(--p-highlight-background);
    color: var(--p-highlight-color);
    font-weight: 600;
}

.mode-content {
    /* Make sure the menu is above the button    */
    position: absolute;
    bottom: 50px;
}

.menu {
    border: none;
    width: fit-content;
}

.moving-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
}

.moving-overlay p {
    margin: 0;
    color: var(--text-color);
    font-weight: 500;
}

.select-organization {
    text-decoration: none;
}
</style>
